import { Box, Button } from '@mui/material'
import { Trans } from '@lingui/macro'
import { FC } from 'react'
import { ContactFields } from './ContactFields'
import Loading from 'egret/components/EgretLoadable/Loading'
import * as yup from 'yup'
import CustomButton from '../CustomButton'
import { AlertBanner } from 'app/views/common/alerts/AlertBanner'
import { ContactDialogType } from './useContactDialog'
import { isNil } from 'lodash'

interface ContactFieldsSearchProps {
  loading: boolean
  firstName?: string
  lastName?: string
  email?: string
  onSearch?: (values: { firstName?: string, lastName?: string, email?: string }) => void
  onBack?: () => void
  type?: ContactDialogType
  role?: string
}

export const ContactFieldsSearch: FC<ContactFieldsSearchProps> = ({
  loading,
  firstName = '',
  lastName = '',
  email = '',
  onSearch,
  onBack,
  type = 'contact-picker',
  role
}) => {
  return (
    <ContactFields
      hiddenFields={{
        providedOrganizationName: true,
        jobTitle: true,
        phoneNumber: true,
        phoneNumberCountryCode: true,
        changeReason: true
      }}
      initialValues={{
        firstName,
        lastName,
        email
      }}
      sx={{
        marginBottom: '-8px'
      }}
      title={
        <>
          <Box
            sx={{
              marginTop: '18px',
              marginBottom: '48px',
              fontWeight: 'bold',
              fontSize: 24,
              lineHeight: '28px',
              color: 'primary.main',
              textAlign: 'center'
            }}
          >
            {type === 'project-affiliations' && !isNil(role)
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  <Trans>PROJECT_AFFILIATIONS_ADD_TITLE</Trans>
                  <Box>{role}</Box>
                </Box>)
              : <Trans>ADD_LEGAL_CONTACT_TITLE</Trans>}
          </Box>
          <Box
            sx={{
              marginBottom: '25px',
              fontWeight: 'bold',
              fontSize: 20,
              lineHeight: '25px',
              color: 'primary.main'
            }}
          >
            <Trans>Search for contact</Trans>
          </Box>
          <AlertBanner
            title={<Trans>SEARCH_CONTACT_BANNER_TITLE</Trans>}
            description={<Trans>SEARCH_CONTACT_BANNER_DESCRIPTION</Trans>}
            sx={{ marginBottom: '24px' }}
          />
          <Box
            sx={{
              fontWeight: 600,
              fontSize: 12,
              lineHeight: '16px',
              marginBottom: '-16px'
            }}
          >
            <Trans>SEARCH_CONTACT_DESCRIPTION</Trans>
          </Box>
        </>
      }
    >
      {({ values: { firstName, lastName, email }, isValid }) => {
        const firstNameTrimmed = firstName?.trim() ?? ''
        const lastNameTrimmed = lastName?.trim() ?? ''
        const nameProvided = Boolean(firstNameTrimmed) && Boolean(lastNameTrimmed)
        const trimmedEmail = email?.trim() ?? ''
        const isValidEmail = trimmedEmail !== '' ? yup.string().email().isValidSync(trimmedEmail) : false
        const isValidated = nameProvided || isValidEmail

        return (
          <>
            <Box sx={{ display: 'flex', gap: '16px', marginBottom: '2px', marginTop: '40px' }}>
              <Button
                disabled={!isValidated || loading}
                fullWidth
                color='primary'
                variant='contained'
                onClick={() => {
                  void onSearch?.({
                    ...(firstNameTrimmed !== '' ? { firstName: firstNameTrimmed } : {}),
                    ...(lastNameTrimmed !== '' ? { lastName: lastNameTrimmed } : {}),
                    ...(trimmedEmail !== '' ? { email: trimmedEmail } : {})
                  })
                }}
              >
                <Trans>Search</Trans>
              </Button>
              <CustomButton
                variant='outlined'
                sx={{ height: '35px' }}
                onClick={onBack}
              >
                <Trans>BACK_BTN</Trans>
              </CustomButton>
            </Box>
            {loading && (
              <div style={{ marginTop: 15, height: 100 }}>
                <Loading isNotFixed />
              </div>
            )}
          </>
        )
      }}
    </ContactFields>
  )
}
